import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Mail, Check, Info, FileText, Compass, ExternalLink } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { locItem, money, Qty } from "@/components/campaign/ServiceCatalog";
import { TECH_EMAIL } from "@/lib/analysisMail";
import { AdvFormats } from "@/components/campaign/AdvFormats";
import { TryAppButton } from "@/components/home/TryAppButton";

export const isQuote = (it) => it.price_max > it.price;
const chf = (n) => `CHF ${Number(n).toLocaleString("de-CH")}`;

export const priceLabel = (it, tr) => {
  const per = it.price_type === "mensile" ? tr("/mese", "/month") : it.price_type === "annuale" ? ` / ${tr("anno", "year")}` : it.price_type === "setup" ? ` ${tr("setup", "setup")}` : it.price_type === "sessione" ? ` ${tr("a sessione", "per session")}` : "";
  return isQuote(it) ? `${tr("da", "from")} ${chf(it.price)}–${Number(it.price_max).toLocaleString("de-CH")}${per}` : `${chf(it.price)}${per}`;
};

const mailto = (subject, lines) => `mailto:${TECH_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.filter(Boolean).join("\n"))}`;

const commonNotes = (tr) => [
  tr("Account e contenuti restano di proprietà del cliente", "Accounts and content remain the client's property"),
  tr("Prezzi da validare prima della pubblicazione", "Prices to be validated before publication"),
  tr("Nessuna promessa di numeri di contatti: promettiamo metodo, trasparenza e report", "No promise of contact numbers: we promise method, transparency and reports"),
];

const SITES = { "agente-ai": "https://www.graficawebdesign.com" };

const PackageCard = ({ it }) => {
  const { lang, tr } = useLang();
  const { user } = useAuth();
  const { cart, setQty } = useCart();
  const l = locItem(it, lang);
  const qty = cart[it.id] || 0;
  const price = priceLabel(it, tr);
  const body = [`${tr("Pacchetto", "Package")}: ${it.name}`, `${tr("Prezzo", "Price")}: ${price}`, user && `\n${tr("Cliente", "Client")}: ${user.name || ""}`, user && `Email: ${user.email}`, user?.phone && `${tr("Telefono", "Phone")}: ${user.phone}`];
  const add = () => {
    setQty(it.id, 1);
    toast.success(tr("Aggiunto al carrello", "Added to cart"), { description: tr("Analizza il tuo sito qui sopra per completare l'ordine.", "Analyse your website above to complete the order.") });
  };
  return (
    <article id={`package-${it.id}`} data-testid={`package-card-${it.id}`} className={`snap-start flex-shrink-0 w-[85vw] sm:w-[340px] md:w-auto flex flex-col bg-white border p-5 transition-[border-color,transform] duration-300 hover:-translate-y-1 ${qty ? "border-[#E0262C]" : "border-[#cfd6d9] hover:border-[#2B3036]"}`}>
      <span className="text-[9px] uppercase tracking-widest font-mono text-[#E0262C]">{l.category || it.category}</span>
      <h3 className="font-display text-lg leading-tight mt-1">{l.name}</h3>
      <p className="text-xs text-[#5b6168] mt-1">{l.target || it.target}</p>
      <ul className="mt-3 space-y-1.5 flex-1">
        {(l.includes || []).slice(0, 5).map((x) => <li key={x} className="flex gap-1.5 text-xs text-[#27272a]"><Check className="w-3.5 h-3.5 text-[#10b981] flex-shrink-0 mt-px" />{x}</li>)}
      </ul>
      {it.id === "adv-plus" && <AdvFormats />}
      <div className="mt-4 pt-3 border-t border-[#e4e4e7]">
        {it.price_type === "annuale" ? (<>
          <div data-testid={`package-price-${it.id}`} dir="ltr" className="font-display text-xl">CHF {Math.round(it.price / 365)} <span className="text-sm">/ {tr("giorno", "day")}</span></div>
          <div className="text-[10px] font-mono text-[#71717a]">{tr("Pagamento anticipato", "Paid in advance")}: {price}</div>
        </>) : <div data-testid={`package-price-${it.id}`} dir="ltr" className="font-display text-xl">{price}</div>}
        {isQuote(it) && <span className="inline-block mt-1 text-[9px] uppercase font-mono tracking-widest px-1.5 py-0.5 bg-[#2B3036] text-white">{tr("Su preventivo", "On quotation")}</span>}
        {!isQuote(it) && it.min_months > 1 && <div className="text-[10px] font-mono text-[#71717a]">{tr("Pagamento anticipato", "Paid in advance")}: {it.min_months} {tr("mesi", "months")} = {money(it.price * it.min_months)}</div>}
        {it.extra && <div className="text-[11px] text-[#5b6168] mt-1">{l.extra || it.extra}</div>}
      </div>
      <ul className="mt-3 space-y-0.5">
        {[...(l.notes || []), ...commonNotes(tr)].map((n) => <li key={n} className="flex gap-1 text-[10px] leading-snug text-[#71717a]"><Info className="w-3 h-3 flex-shrink-0 mt-px" />{n}</li>)}
      </ul>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {it.id === "web-app" && <TryAppButton />}
        {SITES[it.id] ? (
          <a data-testid={`package-site-${it.id}`} href={SITES[it.id]} target="_blank" rel="noopener noreferrer" className="h-10 px-4 text-sm font-medium inline-flex items-center gap-1.5 bg-[#E0262C] text-white hover:bg-[#2B3036] transition-colors"><ExternalLink className="w-4 h-4" /> {tr("Vai al sito", "Visit the site")}</a>
        ) : isQuote(it) ? (
          <a data-testid={`package-quote-${it.id}`} href={mailto(`${tr("Richiesta preventivo", "Quote request")} ${it.name}`, body)} className="h-10 px-4 text-sm font-medium inline-flex items-center gap-1.5 bg-[#2B3036] text-white hover:bg-[#E0262C] transition-colors"><FileText className="w-4 h-4" /> {tr("Richiedi preventivo", "Request a quote")}</a>
        ) : qty ? <Qty qty={qty} onChange={(q) => setQty(it.id, q)} testid={`package-qty-${it.id}`} /> : (
          <button type="button" data-testid={`package-buy-${it.id}`} onClick={add} className="h-10 px-4 text-sm font-medium inline-flex items-center gap-1.5 bg-[#2B3036] text-white hover:bg-[#E0262C] transition-colors"><Plus className="w-4 h-4" /> {tr("Acquista", "Buy")}</button>
        )}
        <a data-testid={`package-tech-${it.id}`} href={mailto(`Richiesta informazioni ${it.name}`, body)} className="h-10 px-3 text-xs font-medium inline-flex items-center gap-1.5 border border-[#2B3036] hover:bg-[#f4f6f7] transition-colors"><Mail className="w-3.5 h-3.5" /> {tr("Parla con un tecnico", "Talk to a technician")}</a>
      </div>
    </article>
  );
};

const guide = (tr) => [
  [tr("Parto da zero", "Starting from scratch"), tr("Presenza Base / Sito Monopagina", "Basic Presence / One-page Website"), "CHF 599", null],
  ["WEB/APP", tr("Presenza Base / Web+App", "Basic Presence / Web+App"), tr("CHF 730 / anno", "CHF 730 / year"), "web-app"],
  [tr("Ho un sito che non rende", "My site doesn't perform"), "Audit & Restart › SEO Locale Start", tr("gratis / CHF 300/mese", "free / CHF 300/month"), "audit-restart"],
  [tr("Voglio contatti subito", "I want contacts now"), "ADV Locale", tr("CHF 350/mese + budget", "CHF 350/month + budget"), "adv-locale"],
  [tr("Voglio crescere e farmi citare dalle AI", "I want to grow and be cited by AI"), "SEO Locale Pro / GEO", tr("da CHF 500/mese", "from CHF 500/month"), "seo-locale-pro"],
  [tr("Presenza social costante", "Consistent social presence"), "Social Start / Crescita", tr("CHF 350/mese", "CHF 350/month"), "social-start"],
  [tr("Vendo online", "I sell online"), "E-commerce Starter", "CHF 2'500", "ecommerce-starter"],
  [tr("Niente pensieri tecnici", "No technical worries"), "Tranquillità / Crescita Continua", tr("CHF 49/mese", "CHF 49/month"), "tranquillita"],
  [tr("Delego tutto", "I delegate everything"), tr("Visibilità Completa", "Complete Visibility"), tr("CHF 1'200/mese", "CHF 1,200/month"), "visibilita-completa"],
];

const Guide = () => {
  const { tr } = useLang();
  const go = (id) => id && document.querySelector(`[data-testid="package-card-${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
  return (
    <div data-testid="package-guide" className="border border-[#2B3036] bg-[#2B3036] text-white p-5 mb-8">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-mono text-[#E0262C] mb-3"><Compass className="w-3.5 h-3.5" /> {tr("Quale pacchetto scegliere", "Which package to choose")}</div>
      <ul className="md:hidden space-y-2">{guide(tr).map(([need, pkg, from, id]) => (
        <li key={need} className="border-t border-[#3f454c] pt-2 text-sm"><div className="text-[#c4c9ce] text-xs">{need}</div>
          <div className="flex justify-between gap-2"><button type="button" onClick={() => go(id)} className="text-left underline decoration-[#E0262C] underline-offset-4">{pkg}</button><span className="font-mono text-xs whitespace-nowrap" dir="ltr">{from}</span></div></li>
      ))}</ul>
      <div className="overflow-x-auto hidden md:block">
        <table className="w-full text-sm min-w-[560px]">
          <thead><tr className="text-left text-[10px] uppercase font-mono text-[#a1a1aa]"><th className="py-1.5 pr-4">{tr("Esigenza", "Need")}</th><th className="pr-4">{tr("Pacchetto", "Package")}</th><th>{tr("Da", "From")}</th></tr></thead>
          <tbody>{guide(tr).map(([need, pkg, from, id]) => (
            <tr key={need} className="border-t border-[#3f454c]">
              <td className="py-2 pr-4 text-[#c4c9ce]">{need}</td>
              <td className="pr-4">{id ? <button type="button" data-testid={`guide-go-${id}`} onClick={() => go(id)} className="underline decoration-[#E0262C] underline-offset-4 hover:text-[#E0262C]">{pkg}</button> : pkg}</td>
              <td className="font-mono text-xs" dir="ltr">{from}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
};

export const PackageCards = () => {
  const { tr } = useLang();
  const { catalog } = useCart();
  const [cat, setCat] = useState("all");
  useEffect(() => {
    if (catalog && window.location.hash.startsWith("#package-")) setTimeout(() => document.querySelector(window.location.hash)?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
  }, [catalog]);
  const pkgs = (catalog || []).filter((i) => i.kind === "package");
  if (!pkgs.length) return null;
  const cats = [...new Set(pkgs.map((p) => p.category))];
  const shown = cat === "all" ? pkgs : pkgs.filter((p) => p.category === cat);
  return (
    <section data-testid="package-cards" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-[#E0262C] mb-3">{tr("Pacchetti", "Packages")}</div>
      <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-6">{tr("Pacchetti e prezzi trasparenti", "Transparent packages and prices")}</h2>
      <Guide />
      <div className="flex flex-wrap gap-2 mb-5">
        {["all", ...cats].map((c) => (
          <button key={c} type="button" data-testid={`package-filter-${c === "all" ? "all" : c.replace(/\W+/g, "-").toLowerCase()}`} onClick={() => setCat(c)}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${cat === c ? "bg-[#2B3036] text-white border-[#2B3036]" : "border-[#cfd6d9] hover:border-[#2B3036]"}`}>{c === "all" ? tr("Tutti", "All") : c}</button>
        ))}
      </div>
      <div className="flex md:grid md:grid-cols-3 xl:grid-cols-4 gap-4 overflow-x-auto md:overflow-visible snap-x snap-mandatory pb-3 -mx-6 px-6 md:mx-0 md:px-0">
        {shown.map((it) => <PackageCard key={it.id} it={it} />)}
      </div>
    </section>
  );
};
